(ns hai.notes.data
  "Note mutations and optimistic edit state. Every mutation returns the fresh
   note (the shared editor shape) and stores it at the entity ident the detail
   page reads; the wall re-queries its rows and facet counts when mounted,
   since a paged, filtered collection can't be reconciled from one note."
  (:require
   [clojure.string :as str]
   [hai.notes.mutations :as mutations]
   [hai.ui.rf-sugar :refer [defevent-fx defsub]]))

(def table-id
  "Collection id of the notes wall. The mutations re-resolve it when mounted."
  ::table)

(defn- note-ident
  [note-uuid]
  [:note/uuid (uuid (str note-uuid))])

(defn- wall-refresh-fx
  [db]
  (cond-> []
    (get-in db [:collections table-id])
    (conj [:dispatch [:table/resolve table-id]]
          [:dispatch [::load-facets]])))

(defevent-fx ::delete [{:keys [db]} note-uuid]
  {:resolve (assoc (mutations/delete-resolve note-uuid)
                   :on-success-fx (conj (wall-refresh-fx db)
                                        [:dispatch [:entity/clear {:note/uuid note-uuid}]]))})

(defevent-fx ::save [{:keys [db]} {note-uuid :note/uuid note-text :note/text}]
  {:resolve (assoc (mutations/upsert-resolve {:note/uuid note-uuid :note/text note-text})
                   :store-in (note-ident note-uuid)
                   :on-success-fx (wall-refresh-fx db))})

(defevent-fx ::add-link [{:keys [db]} {note-uuid :note/uuid link-url :url}]
  ;; Re-adding a URL lifts any leftover optimistic-removal mark for it.
  {:db (update-in db [::removing-links note-uuid] (fnil disj #{}) link-url)
   :resolve (assoc (mutations/upsert-resolve {:note/uuid note-uuid
                                              :activity/urls [link-url]})
                   :store-in (note-ident note-uuid)
                   :on-success-fx (wall-refresh-fx db))})

;; Optimistic: the row hides immediately via ::removing-links while the table
;; still holds the stale activity; the re-resolve catches the data up, and an
;; error unhides the row.
(defevent-fx ::remove-link [{:keys [db]} {note-uuid :note/uuid link-url :url}]
  {:db (update-in db [::removing-links note-uuid] (fnil conj #{}) link-url)
   :resolve (assoc (mutations/remove-link-resolve note-uuid link-url)
                   :store-in (note-ident note-uuid)
                   :on-success-fx (wall-refresh-fx db)
                   :on-error-fx [[:dispatch [::remove-link-failed {:note/uuid note-uuid :url link-url}]]])})

(defevent-fx ::remove-link-failed [{:keys [db]} {note-uuid :note/uuid link-url :url}]
  {:db (update-in db [::removing-links note-uuid] (fnil disj #{}) link-url)
   :fx [[:dispatch [:toast/error {:text "Could not remove link. Try again."}]]]})

;; Tag removal mirrors link removal: optimistic hide via ::removing-tags, detach
;; through note/upsert (`:note/untag-uuids`), re-resolve, and unhide on error.
(defevent-fx ::remove-tag [{:keys [db]} {note-uuid :note/uuid tag-uuid :group/uuid}]
  {:db (update-in db [::removing-tags note-uuid] (fnil conj #{}) tag-uuid)
   :resolve (assoc (mutations/upsert-resolve {:note/uuid note-uuid
                                              :note/untag-uuids [tag-uuid]})
                   :store-in (note-ident note-uuid)
                   :on-success-fx (wall-refresh-fx db)
                   :on-error-fx [[:dispatch [::remove-tag-failed {:note/uuid note-uuid :group/uuid tag-uuid}]]])})

(defevent-fx ::remove-tag-failed [{:keys [db]} {note-uuid :note/uuid tag-uuid :group/uuid}]
  {:db (update-in db [::removing-tags note-uuid] (fnil disj #{}) tag-uuid)
   :fx [[:dispatch [:toast/error {:text "Could not remove tag. Try again."}]]]})

(defevent-fx ::add-tag [{:keys [db]} {note-uuid :note/uuid :keys [tag-uuid tag-name]}]
  ;; Re-adding a tag lifts any leftover optimistic-removal mark for it, so it
  ;; shows again once the re-resolve lands (mirrors ::add-link).
  {:db (cond-> db
         tag-uuid (update-in [::removing-tags note-uuid] (fnil disj #{}) tag-uuid))
   :resolve (assoc (mutations/upsert-resolve (cond-> {:note/uuid note-uuid}
                                               tag-uuid (assoc :note/tag-uuids [tag-uuid])
                                               tag-name (assoc :note/tag-names [tag-name])))
                   :store-in (note-ident note-uuid)
                   :on-success-fx (wall-refresh-fx db))})

;; Contact removal mirrors tag/link removal: optimistic hide via
;; ::removing-contacts, detach through note/remove-contact, re-resolve, and
;; unhide on error.
(defevent-fx ::remove-contact [{:keys [db]} {note-uuid :note/uuid contact-uuid :contact/uuid}]
  {:db (update-in db [::removing-contacts note-uuid] (fnil conj #{}) contact-uuid)
   :resolve (assoc (mutations/remove-contact-resolve note-uuid contact-uuid)
                   :store-in (note-ident note-uuid)
                   :on-success-fx (wall-refresh-fx db)
                   :on-error-fx [[:dispatch [::remove-contact-failed {:note/uuid note-uuid :contact/uuid contact-uuid}]]])})

(defevent-fx ::remove-contact-failed [{:keys [db]} {note-uuid :note/uuid contact-uuid :contact/uuid}]
  {:db (update-in db [::removing-contacts note-uuid] (fnil disj #{}) contact-uuid)
   :fx [[:dispatch [:toast/error {:text "Could not remove contact. Try again."}]]]})

(defevent-fx ::add-contact [{:keys [db]} {note-uuid :note/uuid contact-uuid :contact/uuid}]
  ;; Re-adding a contact lifts any leftover optimistic-removal mark for it,
  ;; so it shows again once the re-resolve lands (mirrors ::add-link).
  {:db (update-in db [::removing-contacts note-uuid] (fnil disj #{}) contact-uuid)
   :resolve (assoc (mutations/upsert-resolve {:note/uuid note-uuid :note/contact-uuids [contact-uuid]})
                   :store-in (note-ident note-uuid)
                   :on-success-fx (wall-refresh-fx db))})

;; Linked notes and tasks follow the contact pattern exactly: optimistic hide,
;; detach through their own mutation, re-resolve, unhide on error. Note links
;; are one-way, so only the note being edited changes.
(defevent-fx ::remove-note-link [{:keys [db]} {note-uuid :note/uuid linked-uuid :note/linked-uuid}]
  {:db (update-in db [::removing-notes note-uuid] (fnil conj #{}) linked-uuid)
   :resolve (assoc (mutations/remove-note-resolve note-uuid linked-uuid)
                   :store-in (note-ident note-uuid)
                   :on-success-fx (wall-refresh-fx db)
                   :on-error-fx [[:dispatch [::remove-note-link-failed
                                             {:note/uuid note-uuid :note/linked-uuid linked-uuid}]]])})

(defevent-fx ::remove-note-link-failed [{:keys [db]} {note-uuid :note/uuid linked-uuid :note/linked-uuid}]
  {:db (update-in db [::removing-notes note-uuid] (fnil disj #{}) linked-uuid)
   :fx [[:dispatch [:toast/error {:text "Could not remove linked note. Try again."}]]]})

(defevent-fx ::add-note-link [{:keys [db]} {note-uuid :note/uuid linked-uuid :note/linked-uuid}]
  {:db (update-in db [::removing-notes note-uuid] (fnil disj #{}) linked-uuid)
   :resolve (assoc (mutations/upsert-resolve {:note/uuid note-uuid :note/note-uuids [linked-uuid]})
                   :store-in (note-ident note-uuid)
                   :on-success-fx (wall-refresh-fx db))})

(defevent-fx ::remove-task [{:keys [db]} {note-uuid :note/uuid task-uuid :task/uuid}]
  {:db (update-in db [::removing-tasks note-uuid] (fnil conj #{}) task-uuid)
   :resolve (assoc (mutations/remove-task-resolve note-uuid task-uuid)
                   :store-in (note-ident note-uuid)
                   :on-success-fx (wall-refresh-fx db)
                   :on-error-fx [[:dispatch [::remove-task-failed
                                             {:note/uuid note-uuid :task/uuid task-uuid}]]])})

(defevent-fx ::remove-task-failed [{:keys [db]} {note-uuid :note/uuid task-uuid :task/uuid}]
  {:db (update-in db [::removing-tasks note-uuid] (fnil disj #{}) task-uuid)
   :fx [[:dispatch [:toast/error {:text "Could not remove linked task. Try again."}]]]})

(defevent-fx ::add-task [{:keys [db]} {note-uuid :note/uuid task-uuid :task/uuid}]
  {:db (update-in db [::removing-tasks note-uuid] (fnil disj #{}) task-uuid)
   :resolve (assoc (mutations/upsert-resolve {:note/uuid note-uuid :note/task-uuids [task-uuid]})
                   :store-in (note-ident note-uuid)
                   :on-success-fx (wall-refresh-fx db))})

;; Clicking a linked contact/note/task chip goes to that entity's own page. Every
;; route takes the entity map itself, the way the omnibar's search results do —
;; routed by the id attr the chip's entity carries.
;;
;; Contacts have no read view, only `:contacts/edit`, whose save and cancel both
;; navigate to the contacts table — which would strand someone who arrived from a
;; note. `:return-to` carries the current path so that page can send them back
;; where they came from instead.
(defevent-fx ::open-entity [{:keys [db]} entity]
  (let [return-to (get-in db [:routing/current-route :path])]
    {:dispatch [:routing/navigate
                (cond
                  (:contact/uuid entity) [:contacts/edit entity
                                          (cond-> {} return-to (assoc :return-to return-to))]
                  (:task/uuid entity)    [:tasks/detail entity]
                  :else                  [:notes/detail entity])]}))

;; `or … #{}` (not get-in's default) because a cleared entry can hold an explicit
;; nil, which the default wouldn't replace — and a nil predicate blows up `remove`.
(defsub ::removing-links [db note-uuid]
  (or (get-in db [::removing-links note-uuid]) #{}))

(defsub ::removing-tags [db note-uuid]
  (or (get-in db [::removing-tags note-uuid]) #{}))

(defsub ::removing-contacts [db note-uuid]
  (or (get-in db [::removing-contacts note-uuid]) #{}))

(defsub ::removing-notes [db note-uuid]
  (or (get-in db [::removing-notes note-uuid]) #{}))

(defsub ::removing-tasks [db note-uuid]
  (or (get-in db [::removing-tasks note-uuid]) #{}))

(defsub ::saving? [db note-uuid]
  (contains? (get db ::saving #{}) note-uuid))

(defsub ::saved-at [db note-uuid]
  (get-in db [::saved-at note-uuid]))

(def ^:private facets-eql
  [:facet/total
   :facet/no-web-page-count
   :facet/not-shared-count
   :facet/untagged-count
   :facet/no-contact-count
   {:facet/tags [:tag/name :facet/count]}
   {:facet/contacts [:contact/uuid :contact/full-name :facet/count]}
   {:facet/domains [:domain/name :facet/count
                    {:domain/urls [:url/value :url/label :facet/count]}]}
   {:facet/shared [:user/uuid :facet/label :facet/count]}])

(defn- live-filters
  "The wall's active filter values, read from the filters SUB-form's fields —
   not the root form's `:table/filters` map. A sub-field write lands in the
   sub-form's field state synchronously but reaches the root map only through
   a chain of queued events, so a facet-click handler dispatching
   [[::load-facets]] right after `set-value!` would read a stale root."
  [db]
  (into {}
        (keep (fn [[attr {current-value :form.field/current-value}]]
                (when (some? current-value)
                  [attr current-value])))
        (get-in db [:forms [table-id :table/filters] :form/fields])))

;; Facet option lists + SMART counts for the filter sidebar
;; (`hai.pathom.notes/user-note-facets`): each dimension's counts respect the
;; wall's live search + the OTHER dimensions' filters, sent as query params.
;; Loaded on sidebar mount, after note mutations, and after every filter or
;; (debounced) search change.
(defevent-fx ::load-facets
  [{:keys [db]} _]
  (let [active (live-filters db)
        search (get-in db [:forms table-id :form/fields
                           :table/search :form.field/current-value])
        params (cond-> {}
                 (seq active) (assoc :filters active)
                 (some-> search str/trim not-empty) (assoc :search search))]
    {:resolve {:query        [(if (seq params)
                                (list {:user/note-facets facets-eql} params)
                                {:user/note-facets facets-eql})]
               :transform-fn :user/note-facets
               :store-in     [::facets]}}))

;; Search keystrokes funnel through this so the counts re-query once typing
;; pauses — the same cadence as the table's own debounced resolve.
(defevent-fx ::load-facets-debounced
  [_ _]
  {:debounce/dispatch {:id ::load-facets
                       :event [::load-facets]
                       :duration 500}})

(defsub ::facets [db]
  (get db ::facets))
